import { Injectable } from '@angular/core';
import { HttpClientService } from '../httpClient/httpclient.service';
import { User, UserComplex, UserCompany, UserApplication } from '../../models/user.model';
import { RolesService } from './roles.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { publishReplay, refCount } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable'
import 'rxjs/add/operator/mergeMap';

@Injectable()
export class UsersService {
  users: User[];
  userCompanies: UserCompany[];
  userApplications: UserApplication[];
  userEditing: UserComplex;
  private cachedFields: Observable<any>;
  private companyCachedFields: Observable<any>;
  private applicationCachedFields: Observable<any>;
  private roles: any[] = [];
  passwordResetSuccessMsg: string;

  constructor (
    private h: HttpClientService,
    private srvNotify: MessageService,
    private srvRoles: RolesService,
    private translateSvc: TranslateService
  ) {
    this.users = [];
    translateSvc.get(['users.pswResetSuccess']).subscribe(res => this.passwordResetSuccessMsg = res['users.pswResetSuccess']);
  }

  getRoles() {
    return new Promise((resolve, reject) => {
      this.srvRoles.getAll().subscribe(
        res => this.roles = res,
        err => console.error(err),
        () => resolve()
      );
    })
  }

  list(
    offset = null,
    limit = null,
    search?: Array<{ key: string, value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<any> {
    return Observable.fromPromise(this.getRoles()).mergeMap(() => {
      const params = HttpClientService.buildListParams(offset, limit, search, globalSearch, sort, sort_dir);
      return this.h.getPaged('/koncentro-auth/users/getUserList' +
        (params.toString() ? '?' + params.toString().replace(/\+/gi, '%2B').replace(/'/gi, "''") : '' ))
        .map(
          res => {
            res.data.map(
              user => {
                let roleDesc = (this.roles.filter(r => r.id === user.role_id))[0].descr;
                Object.assign(user, {role_desc: roleDesc});
              }
            );
            return res;
          }
        )
    })
  }

  getAll() {
    return this.h.get('/koncentro-auth/users')
      .map(res => {
        this.srvRoles.getAll().subscribe();
        return this.users = res;
      });
  }

  getList() {
    return this.h.get('/koncentro-auth/users/getUserList')
      .map(res => {
        this.srvRoles.getAll().subscribe();
        return this.users = res;
      });
  }

  get(id: number) {
    return this.h.get('/koncentro-auth/users/' + id);
  }

  getFiltereIdList(ids: string) {
    return this.h.get('/koncentro-auth/users?id_list=' + ids);
  }

  delete(record) {
    return this.h.delete('/koncentro-auth/users/' + record.id)
      .map(res => {
        this.users.splice(this.users.indexOf(record), 1);
        this.srvNotify.add({severity: 'info', summary: 'Confirmed', detail: 'User deleted'});
      });
  }

  post(newData: UserComplex) {
    console.log(newData);
    return this.h.post('/koncentro-auth/users/', newData)
      .map(res => {
        newData.user.id = res.id;
        newData.user.role_desc = this.srvRoles.roles.find(el => el.id == newData.user.role_id).descr;
        this.users.push(newData.user);
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });

  }

  clone(newData: UserComplex, userToCloneId: number) {
    return this.h.post('koncentro-auth/users/clone_user/' + userToCloneId, newData);
  }

  put(newData: UserComplex) {
    console.log('User PUT');
    console.log(newData);
    return this.h.put('/koncentro-auth/users/', newData.user)
      .map(res => {
        if (this.srvRoles.roles.find(el => el.id == newData.user.role_id) != null) {
          newData.user.role_desc = this.srvRoles.roles.find(el => el.id == newData.user.role_id).descr;
          Object.assign(this.userEditing.user, newData.user);
        }
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  resetPassword(u: User) {
    return this.h.get('/koncentro-auth/users/reset_password/' + u.id)
      .map(res => {
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Password succesfully resetted and mail sent to user'});
      });
  }

  newResetPassword(userId:number, newPsw: string) {
    return this.h.get(`/koncentro-auth/users/reset_password/${userId}/${newPsw}`)
      .map(res => {
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: this.passwordResetSuccessMsg});
      });
  }

  createUserCompany(uc: UserCompany) {
    return this.h.post('/koncentro-auth/users/user_companies', uc)
      .map (res => {
        uc.id = res.id;
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  updateUserCompany(uc: UserCompany) {
    return this.h.put('/koncentro-auth/users/user_companies/' + uc.id, uc)
      .map (res => {
        Object.assign(this.userCompanies[this.userCompanies.findIndex(uC => uC.id == uc.id)], uc);
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  deleteUserCompany(id) {
    return this.h.delete('/koncentro-auth/users/user_companies/' + id)
      .map (res => {
        this.userCompanies.splice(this.userCompanies.findIndex(uc => uc.id == id), 1);
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  getUserCompanies(u: User) {
    return this.h.get('/koncentro-auth/users/user_companies/' + u.id)
      .map (res => {

        this.userCompanies = res;
        this.userCompanies.forEach(function (uc) {
          uc.date_end = new Date(uc.date_end);
          uc.date_start = new Date(uc.date_start);
        })
      });
  }

  changePassword(p: string, pc: string) {
    return this.h.post('/koncentro-auth/users/change_password', { password: p, password_confirm: pc})
      .map (res => {
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  getUserApplications(u: User){
    return this.h.get('/koncentro-auth/users/user_companies_applications/' + u.id)
      .map (res => {
        console.log(res)
        this.userApplications = res;
        this.userApplications.forEach(function (uc) {
          uc.date_end = new Date(uc.date_end);
          uc.date_start = new Date(uc.date_start);
        })
      });
  }

  createUserApplication(uc: UserApplication) {
    return this.h.post('/koncentro-auth/users/user_companies_applications', uc)
      .map (res => {
        uc.id = res.id;
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  deleteUserApplication(id) {
    return this.h.delete('/koncentro-auth/users/user_companies_applications/' + id)
      .map (res => {
        this.userApplications.splice(this.userApplications.findIndex(uc => uc.id == id), 1);
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  updateUserApplication(uc) {
    return this.h.put('/koncentro-auth/users/user_companies_applications/' + uc.id, uc)
      .map (res => {
        Object.assign(this.userApplications[this.userApplications.findIndex(uC => uC.id == uc.id)], uc);
        this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  getApplicationsForUserCompany(userId: number, companyId: number){
    return this.h.get('/koncentro-auth/users/user_companies_applications/' + userId + '/' + companyId)
      .map (res => {
        console.log(res);

        this.userApplications = res;
        this.userApplications.forEach(function (uc) {
          uc.date_end = new Date(uc.date_end);
          uc.date_start = new Date(uc.date_start);
        })
        return res;
      });
  }

  getFields() {
    if (!this.cachedFields) {
      let userDto = this.h.get('/koncentro-auth/schema/UserDto');
      let userPostDto = this.h.get('/koncentro-auth/schema/UserPostDto');
      this.cachedFields = Observable.zip(userDto, userPostDto).pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      );
    }
    return this.cachedFields;
  }

  getCompanyFields() {
    if (!this.companyCachedFields) {
      this.companyCachedFields = this.h.get('/koncentro-auth/schema/UserCompanyDto').pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.companyCachedFields;
  }

  getApplicationFields() {
    if (!this.applicationCachedFields) {
      this.applicationCachedFields = this.h.get('/koncentro-auth/schema/UserCompanyApplicationDto').pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.applicationCachedFields;
  }

  getUserBySurname(name?: string) {
    return this.h.get('/koncentro-auth/users?name=' + name).map(res => res.map(r => ({label: `${r.surname} - ${r.name}`, value: `${r.id}`})))
  }

  getUserByNameSurname(name_surname?: string) {
    return this.h.get('/koncentro-auth/users?name_surname=' + name_surname).map(res => res.map(r => ({label: `${r.surname} - ${r.name}`, value: `${r.id}`})))
  }

  impersonificaUtente(data) {
    return this.h.post(`/koncentro-auth/token/other`, data);
  }

  getUserByNameSurnameUsername(name_surname_username?: string) {
    return this.h.get('/koncentro-auth/users?name_surname_username=' + name_surname_username).map(res => res.map(r => ({label: `${r.surname} - ${r.name}`, value: `${r.id}`})))
  }
}



// WEBPACK FOOTER //
// ./src/app/services/user/users.service.ts